import { useState, useRef, useCallback } from "react";

import type {
  PrinterStatus,
  ReceiptData,
  ShippingLabelData,
  UseThermalPrinterReturn,
} from "../types/printer.types";
import { UUID_PRESETS, BLE_CHUNK_SIZE, BLE_CHUNK_DELAY_MS, ALL_OPTIONAL_SERVICES } from "../constants/printerConfig";
import { buildReceiptBytes } from "../lib/escpos";
import { buildShippingLabelBytes, PreparedShippingImages } from "../lib/escposShipping";

interface UseThermalPrinterOptions {
  presetIndex?: number;
  serviceUuid?: string;
  characteristicUuid?: string;
}

export function useThermalPrinter(
  options: UseThermalPrinterOptions = {}
): UseThermalPrinterReturn {
  const { presetIndex = 0, serviceUuid, characteristicUuid } = options;

  const preset = UUID_PRESETS[presetIndex] ?? UUID_PRESETS[0];
  const resolvedServiceUuid = serviceUuid ?? preset.serviceUuid;
  const resolvedCharUuid = characteristicUuid ?? preset.characteristicUuid;

  const [status, setStatus] = useState<PrinterStatus>("disconnected");

  const deviceRef = useRef<BluetoothDevice | null>(null);
  const charRef   = useRef<BluetoothRemoteGATTCharacteristic | null>(null);

  const handleDisconnect = useCallback((): void => {
    charRef.current = null;
    setStatus("disconnected");
  }, []);

  const writeChunks = useCallback(async (data: Uint8Array): Promise<void> => {
    const char = charRef.current;
    if (!char) throw new Error("Printer not connected.");

    for (let offset = 0; offset < data.length; offset += BLE_CHUNK_SIZE) {
      const chunk = data.slice(offset, offset + BLE_CHUNK_SIZE);
      try {
        await char.writeValueWithoutResponse(chunk);
      } catch {
        await char.writeValueWithResponse(chunk);
      }
      await new Promise<void>((resolve) => setTimeout(resolve, BLE_CHUNK_DELAY_MS));
    }
  }, []);

  const connect = useCallback(async (): Promise<void> => {
    if (!navigator.bluetooth) {
      throw new Error("Web Bluetooth is not supported. Use Chrome or Edge.");
    }

    setStatus("connecting");

    try {
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ALL_OPTIONAL_SERVICES,
      });

      deviceRef.current = device;
      device.addEventListener("gattserverdisconnected", handleDisconnect);

      if (!device.gatt) {
        throw new Error("This device does not expose a GATT server.");
      }

      const server    = await device.gatt.connect();
      const service   = await server.getPrimaryService(resolvedServiceUuid);
      charRef.current = await service.getCharacteristic(resolvedCharUuid);

      setStatus("connected");
    } catch (err) {
      setStatus("disconnected");
      throw err;
    }
  }, [resolvedServiceUuid, resolvedCharUuid, handleDisconnect]);

  const disconnect = useCallback((): void => {
    deviceRef.current?.removeEventListener("gattserverdisconnected", handleDisconnect);
    deviceRef.current?.gatt?.disconnect();
    deviceRef.current = null;
    charRef.current   = null;
    setStatus("disconnected");
  }, [handleDisconnect]);

  const printReceipt = useCallback(
    async (receipt: ReceiptData): Promise<void> => {
      if (!charRef.current) throw new Error("Printer is not connected.");
      const bytes = await buildReceiptBytes(receipt);
      await writeChunks(bytes);
    },
    [writeChunks]
  );

  const printShippingLabel = useCallback(
    async (label: ShippingLabelData, images?: PreparedShippingImages): Promise<void> => {
      if (!charRef.current) throw new Error("Printer is not connected.");
      const bytes = await buildShippingLabelBytes(label, images);
      await writeChunks(bytes);
    },
    [writeChunks]
  );

  return { status, connect, disconnect, printReceipt, printShippingLabel };
}
